import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { StarIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
const TESTIMONIALS = [{
  quote: '“Por primera vez sentí que estaba siguiendo un plan pensado para esta etapa de mi vida.”',
  name: 'Laura',
  age: '52 años',
  country: 'Argentina'
}, {
  quote: '“Necesitaba una forma más simple de acompañar esta etapa sin exigirme como antes.”',
  name: 'María',
  age: '49 años',
  country: 'Argentina'
}, {
  quote: '“Sentí que por fin tenía un recorrido claro para seguir, sin volver a exigirme de la misma manera.”',
  name: 'Carolina',
  age: '47 años',
  country: 'Argentina'
}];
type Testimonial = (typeof TESTIMONIALS)[number];
function TestimonialCard({
  testimonial
}: {
  testimonial: Testimonial;
}) {
  return <motion.div initial={{
    opacity: 0,
    y: 10
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: -10
  }} transition={{
    duration: 0.3,
    ease: 'easeOut'
  }} className="card-premium relative overflow-hidden p-7 md:p-9" data-id="element-411" data-mp="2yexsec9crjje">
      <div className="space-y-7" data-id="element-412" data-mp="2uz80vljmmp6k">
        {/* Stars */}
        <div className="flex gap-1" role="img" aria-label="Testimonio de alumna" data-id="element-413" data-mp="2q1q2gp38jsct">
          {[...Array(5)].map((_, index) => <StarIcon key={index} className="h-4 w-4 text-secondary fill-secondary" data-id="element-414" data-mp="34qm6mb268l85" data-mpi={index} />)}
        </div>

        {/* Quote */}
        <p className="font-body text-[17px] leading-[1.7] italic text-foreground/90 md:text-[19px]" data-id="element-415" data-mp="2zund691zkd0v">
          {testimonial.quote}
        </p>

        {/* Author */}
        <div className="border-t border-border/50 pt-5" data-id="element-416" data-mp="2756lxeq3r268">
          <p className="font-display text-lg font-bold text-gold-light" data-id="element-417" data-mp="2m1fj8aqfn5g0">
            {testimonial.name}
          </p>

          <p className="mt-1 font-body text-sm text-foreground/55" data-id="element-418" data-mp="2h0ruxwtl92ph">
            {testimonial.age} · {testimonial.country}
          </p>
        </div>
      </div>
    </motion.div>;
}
export function TestimonialsSection() {
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const current = TESTIMONIALS[activeTestimonial];
  const previousTestimonial = () => {
    setActiveTestimonial(currentIndex => currentIndex === 0 ? TESTIMONIALS.length - 1 : currentIndex - 1);
  };
  const nextTestimonial = () => {
    setActiveTestimonial(currentIndex => currentIndex === TESTIMONIALS.length - 1 ? 0 : currentIndex + 1);
  };
  return <section className="bg-gradient-section px-4 py-12 md:px-8 md:py-16" data-id="element-419" data-mp="ib9q8c28js3m">
      <div className="mx-auto max-w-[1400px] space-y-10 md:space-y-12" data-id="element-420" data-mp="3guzvmvjqlull">

        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="space-y-3 text-center" data-id="element-421" data-mp="3ltjjx2lszih4">
          <p className="text-secondary text-[10px] font-bold uppercase tracking-[0.24em] md:text-xs" data-id="element-422" data-mp="2y372n14o49dv">
            Lo que cuentan mujeres que hicieron H.E.R.
          </p>

          <h2 className="font-display text-3xl font-black leading-tight md:text-4xl" data-id="element-423" data-mp="330fkt1ptgdf4">
            Experiencias{' '}
            <span className="text-gradient-gold" data-id="element-424" data-mp="1k4tbwpn92rm6">Reales</span>
          </h2>
        </motion.div>

        {/* Testimonial */}
        <div className="mx-auto max-w-2xl" data-id="element-425" data-mp="1b5y5pdk7hhiv">
          <div className="relative" data-id="element-426" data-mp="1b4wlaihavioh">
            <AnimatePresence mode="wait" initial={false} data-id="element-427" data-mp="167d4pbx4qttd">
              {current && <TestimonialCard key={`${current.name}-${activeTestimonial}`} testimonial={current} data-id="element-428" data-mp="1674nx8xvz4cw" />}
            </AnimatePresence>
          </div>

          {/* Navigation */}
          <div className="mt-6 flex items-center justify-center gap-4" data-id="element-429" data-mp="wgryb2b7skzw">
            {/* Previous */}
            <button type="button" onClick={previousTestimonial} aria-label="Testimonio anterior" className="flex h-9 w-9 items-center justify-center rounded-full border border-[#C8A870]/30 text-[#C8A870] transition-all duration-300 hover:border-[#C8A870]/60 hover:bg-[#C8A870]/5" data-id="element-430" data-mp="sgrgr8stjtom">
              <ChevronLeftIcon className="h-4 w-4" data-id="element-431" data-mp="djqql9qa06s9" />
            </button>

            {/* Dots */}
            <div className="flex items-center gap-2" role="tablist" aria-label="Seleccionar testimonio" data-id="element-432" data-mp="2dm01czl1cdc9">
              {TESTIMONIALS.map((testimonial, index) => <button key={testimonial.name} type="button" onClick={() => setActiveTestimonial(index)} role="tab" aria-selected={activeTestimonial === index} aria-label={`Ver testimonio ${index + 1}`} className={`rounded-full transition-all duration-300 ${index === activeTestimonial ? 'h-1.5 w-6 bg-[#C8A870]' : 'h-1.5 w-1.5 bg-[#C8A870]/25 hover:bg-[#C8A870]/50'}`} data-id="element-433" data-mp="2ilw5qzxna6sc" data-mpi={index} />)}
            </div>

            {/* Next */}
            <button type="button" onClick={nextTestimonial} aria-label="Siguiente testimonio" className="flex h-9 w-9 items-center justify-center rounded-full border border-[#C8A870]/30 text-[#C8A870] transition-all duration-300 hover:border-[#C8A870]/60 hover:bg-[#C8A870]/5" data-id="element-434" data-mp="1kwb0qnc8mfb6">
              <ChevronRightIcon className="h-4 w-4" data-id="element-435" data-mp="3f87s6gaojl68" />
            </button>
          </div>
        </div>
      </div>
    </section>;
}